export interface ProviderOnboardingSnapshot {
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  user: {
    avatarUrl: string | null
  } | null
  serviceOfferings: Array<{
    id: string
    customPriceCents?: number | null
    customDurationMinutes?: number | null
    serviceType: {
      id: string
    }
  }>
  availabilitySlots: Array<{ id: string; dayOfWeek: number; startTime: string; endTime: string }>
  serviceAreas: Array<{ id: string; postalCode: string; areaName: string | null; city: string }>
}

export interface OnboardingProfileBasics {
  bio: string
  yearsExperience: string
  languages: string[]
  hourlyRateEur: string
  avatarUrl: string
}

export interface OnboardingServiceDraft {
  selected: boolean
  customPriceEur: string
  customDurationMinutes: string
}

export interface OnboardingAvailabilityDay {
  dayOfWeek: number
  active: boolean
  startTime: string
  endTime: string
}

export interface OnboardingAreaDraft {
  postalCode: string
  areaName: string
  city: string
}

export interface ProviderOnboardingState {
  hydrated: boolean
  step: number
  profileBasics: OnboardingProfileBasics
  services: Record<string, OnboardingServiceDraft>
  availability: OnboardingAvailabilityDay[]
  areas: OnboardingAreaDraft[]
  pendingPostalCode: string
  completedThisSession: boolean
}

export interface LanguageOption {
  code: string
  labelEn: string
  labelEl: string
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'el', labelEn: 'Greek', labelEl: 'Ελληνικά' },
  { code: 'en', labelEn: 'English', labelEl: 'Αγγλικά' },
  { code: 'sq', labelEn: 'Albanian', labelEl: 'Αλβανικά' },
  { code: 'bg', labelEn: 'Bulgarian', labelEl: 'Βουλγαρικά' },
  { code: 'ro', labelEn: 'Romanian', labelEl: 'Ρουμανικά' },
  { code: 'uk', labelEn: 'Ukrainian', labelEl: 'Ουκρανικά' },
  { code: 'de', labelEn: 'German', labelEl: 'Γερμανικά' },
  { code: 'fr', labelEn: 'French', labelEl: 'Γαλλικά' },
]

export const ATHENS_POSTAL_CODES = [
  '10431', '10432', '10433', '10434', '10435', '10436', '10437', '10438', '10439', '10440',
  '11521', '11522', '11523', '11524', '11525', '11526', '11527', '11528',
  '11635', '11636',
  '11741', '11742', '11743', '11744', '11745',
  '15121', '15122', '15123', '15124', '15125', '15126',
  '15232', '15233', '15234', '15235', '15236',
]

export const ONBOARDING_STEP_COUNT = 4

export function createDefaultAvailability(): OnboardingAvailabilityDay[] {
  return [
    { dayOfWeek: 1, active: true, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 2, active: true, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 3, active: true, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 4, active: true, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 5, active: true, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 6, active: false, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 0, active: false, startTime: '09:00', endTime: '17:00' },
  ]
}

export function buildOnboardingState(snapshot: ProviderOnboardingSnapshot | null): ProviderOnboardingState {
  if (!snapshot) {
    return {
      hydrated: true,
      step: 0,
      profileBasics: {
        bio: '',
        yearsExperience: '',
        languages: [],
        hourlyRateEur: '',
        avatarUrl: '',
      },
      services: {},
      availability: createDefaultAvailability(),
      areas: [],
      pendingPostalCode: '',
      completedThisSession: false,
    }
  }

  const availability =
    snapshot.availabilitySlots.length > 0
      ? createDefaultAvailability().map((day) => {
          const slot = snapshot.availabilitySlots.find((item) => item.dayOfWeek === day.dayOfWeek)
          if (!slot) return { ...day, active: false }

          return {
            dayOfWeek: day.dayOfWeek,
            active: true,
            startTime: toTimeInput(slot.startTime),
            endTime: toTimeInput(slot.endTime),
          }
        })
      : createDefaultAvailability()

  const services = Object.fromEntries(
    snapshot.serviceOfferings.map((offering) => [
      offering.serviceType.id,
      {
        selected: true,
        customPriceEur: centsToEuroString(offering.customPriceCents ?? null),
        customDurationMinutes: offering.customDurationMinutes?.toString() ?? '',
      },
    ])
  )

  return {
    hydrated: true,
    step: getFirstIncompleteStep(snapshot),
    profileBasics: {
      bio: snapshot.bio ?? '',
      yearsExperience: snapshot.yearsExperience?.toString() ?? '',
      languages: snapshot.languages,
      hourlyRateEur: centsToEuroString(snapshot.hourlyRateCents),
      avatarUrl: snapshot.user?.avatarUrl ?? '',
    },
    services,
    availability,
    areas: snapshot.serviceAreas.map((area) => ({
      postalCode: area.postalCode,
      areaName: area.areaName ?? '',
      city: area.city,
    })),
    pendingPostalCode: '',
    completedThisSession: false,
  }
}

export function getFirstIncompleteStep(snapshot: ProviderOnboardingSnapshot): number {
  if (!hasProfileBasics(snapshot)) return 0
  if (!hasServices(snapshot)) return 1
  if (!hasAvailability(snapshot)) return 2
  if (!hasServiceAreas(snapshot)) return 3
  return ONBOARDING_STEP_COUNT
}

export function isOnboardingComplete(snapshot: ProviderOnboardingSnapshot | null): boolean {
  return !!snapshot && getFirstIncompleteStep(snapshot) === ONBOARDING_STEP_COUNT
}

export function toTimeInput(value: string) {
  return value.slice(0, 5)
}

export function centsToEuroString(value: number | null | undefined) {
  if (value === null || value === undefined) return ''
  return (value / 100).toFixed(2).replace(/\.00$/, '')
}

function hasProfileBasics(snapshot: ProviderOnboardingSnapshot) {
  return Boolean(
    snapshot.bio?.trim() &&
    snapshot.yearsExperience !== null &&
    snapshot.languages.length > 0 &&
    (snapshot.hourlyRateCents ?? 0) > 0 &&
    snapshot.user?.avatarUrl?.trim()
  )
}

function hasServices(snapshot: ProviderOnboardingSnapshot) {
  return snapshot.serviceOfferings.length > 0
}

function hasAvailability(snapshot: ProviderOnboardingSnapshot) {
  return snapshot.availabilitySlots.length > 0
}

function hasServiceAreas(snapshot: ProviderOnboardingSnapshot) {
  return snapshot.serviceAreas.length > 0
}
